import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import rfdc from 'rfdc';

import {
  IConjointPayload,
  IImageUploadAddPayload,
  IImageUploadRemovePayload,
  IImageUploadReplacePayload,
  IResponseStore,
  IVideoQuestionPayload,
  IVideoQuestionResponse,
} from './interfaces';
import { IDevicePermissions } from '../../../components/fields/videoQuestion/common/interfaces';
import { mixReducers } from '../../reducers/utils';
import oldResponsesReducer from '../../reducers/responses';
import { isArrayOfIImageValues } from './utils';

const cloneDeep = rfdc();
const initialState: IResponseStore = {};

const getFrontendIdAndPosition = (frontendId: string) => {
  const ids = frontendId.split('_');

  return {
    originalFrontendId: ids[0],
    position: ids[1] ? Number(ids[1]) : null,
  };
};

const responsesSlice = createSlice({
  name: 'responses',
  initialState,
  reducers: {
    setConjointResponse(state, action: PayloadAction<IConjointPayload>) {
      const { frontendId, value } = action.payload;
      const { originalFrontendId, position } = getFrontendIdAndPosition(frontendId);
      if (position === null || !originalFrontendId) {
        return state;
      }
      const currentQuestion = state[originalFrontendId];
      if (currentQuestion && 'values' in currentQuestion && Array.isArray(currentQuestion.values)) {
        if (currentQuestion.values.length < position) {
          return;
        }
        (currentQuestion.values ?? []).splice(position, 1, value);
        return;
      }
      state[originalFrontendId] = {
        na: false,
        values: [value],
      };
    },
    deleteConjointResponse(state, action: PayloadAction<string>) {
      const { originalFrontendId, position } = getFrontendIdAndPosition(action.payload);
      if (position === null || !originalFrontendId) {
        return state;
      }

      const currentQuestion = state[originalFrontendId];
      if (currentQuestion && 'values' in currentQuestion && Array.isArray(currentQuestion.values)) {
        (currentQuestion.values ?? []).splice(position, 1);
      }
    },
    setVideoResponse(state, action: PayloadAction<IVideoQuestionPayload>) {
      const { frontendId, value, duration, mimeType, orientation, faceNotVisibleInSeconds } = action.payload;
      const oldResponse = state[frontendId];
      if (oldResponse && !faceNotVisibleInSeconds) {
        state[frontendId] = {
          ...oldResponse,
          value,
          isRecording: false,
          permission: 'granted',
          duration,
          mimeType,
          isSubmissionDeclined: false,
          orientation,
        };
        return;
      }
      const response: IVideoQuestionResponse = {
        value,
        isRecording: false,
        permission: 'granted',
        duration,
        mimeType,
        isSubmissionDeclined: false,
        orientation,
        faceNotVisibleInSeconds,
      };
      state[frontendId] = response;
    },
    setVideoIsRecording(state, action: PayloadAction<{ frontendId: string; isRecording: boolean }>) {
      const { frontendId, isRecording } = action.payload;
      const currentResponse = state[frontendId];
      state[frontendId] = {
        ...currentResponse,
        isRecording,
      };
    },
    setVideoPermission(state, action: PayloadAction<{ frontendId: string; permission?: IDevicePermissions }>) {
      const { frontendId, permission } = action.payload;
      const currentResponse = state[frontendId];
      state[frontendId] = {
        ...currentResponse,
        permission,
      };
    },
    setVideoSubmissionDeclined(state, action: PayloadAction<{ frontendId: string; isSubmissionDeclined: boolean }>) {
      const { frontendId, isSubmissionDeclined } = action.payload;
      const currentResponse = state[frontendId];
      state[frontendId] = {
        ...currentResponse,
        isSubmissionDeclined,
      };
    },
    setVideoFaceNotVisibleInSeconds(
      state,
      action: PayloadAction<{ frontendId: string; faceNotVisibleInSeconds: number }>
    ) {
      const { frontendId, faceNotVisibleInSeconds } = action.payload;
      const currentResponse = state[frontendId];
      state[frontendId] = {
        ...currentResponse,
        faceNotVisibleInSeconds,
      };
    },
    addImagesToImageUploadResponse(state, action: PayloadAction<IImageUploadAddPayload>) {
      const { frontendId, images } = action.payload;
      const oldResponse = cloneDeep(state[frontendId]);
      if (oldResponse && 'values' in oldResponse && isArrayOfIImageValues(oldResponse.values)) {
        const newImages = [...oldResponse.values, ...images];
        state[frontendId] = { values: newImages };
        return;
      }
      state[frontendId] = { values: images };
    },
    removeImageFromImageUploadResponse(state, action: PayloadAction<IImageUploadRemovePayload>) {
      const { frontendId, imageId } = action.payload;
      const oldResponse = cloneDeep(state[frontendId]);
      if (oldResponse && 'values' in oldResponse && isArrayOfIImageValues(oldResponse.values)) {
        const currentImages = [...oldResponse.values];
        const filteredImages = currentImages.filter((image) => image.id !== imageId);
        state[frontendId] = { values: filteredImages };
      }
    },
    replaceImageFromImageUploadResponse(state, action: PayloadAction<IImageUploadReplacePayload>) {
      const { frontendId, image } = action.payload;
      const oldResponse = cloneDeep(state[frontendId]);
      if (oldResponse && 'values' in oldResponse && isArrayOfIImageValues(oldResponse.values)) {
        state[frontendId] = { values: [image] };
        return;
      }
    },
  },
});

export const {
  setConjointResponse,
  deleteConjointResponse,
  setVideoFaceNotVisibleInSeconds,
  setVideoIsRecording,
  setVideoResponse,
  setVideoPermission,
  setVideoSubmissionDeclined,
  addImagesToImageUploadResponse,
  removeImageFromImageUploadResponse,
  replaceImageFromImageUploadResponse,
} = responsesSlice.actions;
export const responsesReducer = mixReducers(responsesSlice.reducer, oldResponsesReducer);
